// libraries
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import tz from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(tz);

import { RRule, Weekday, rrulestr } from "rrule";

// utilities
import { dateOrTimestampAsDate, durationToQuantity, durationToUnit } from "../time";

// types
import { ScheduleCreateForm, ScheduleType } from "../../typings/scheduler/scheduler-types";
import { IndexToMonthStr } from "../../typings/scheduler/scheduler-constants";

// TODO: Test new schedule creation with weekday set and view in edit schedule
function rruleToRecurrenceSection(rule: RRule): ScheduleCreateForm["_recurrence"] {
  const opts = rule.options;

  const bymonth = opts.bymonth?.map((m) => IndexToMonthStr[m - 1]) || [];
  const byweekday = opts.byweekday?.map((w) => w.toString()) || [];
  const bysetpos = opts.bysetpos ? opts.bysetpos[0] : undefined;

  const _ends = opts.count ? "after" : opts.until ? "on" : "never";
  const until = opts.until ? opts.until.toISOString() : undefined;

  return {
    _ends: _ends,
    _weekday: opts.byweekday?.map((wd) => new Weekday(wd).toString()),

    freq: opts.freq,
    interval: opts.interval,
    count: opts.count,
    until: until,
    dtstart: opts.dtstart,
    byweekno: opts.byweekno,
    bymonthday: opts.bymonthday,
    byyearday: opts.byyearday,
    bymonth,
    byweekday,
    bysetpos,
    byhour: opts.byhour ? opts.byhour[0] : undefined,
    bymin: opts.byminute ? opts.byminute[0] : undefined,
  };
}

// Convert a schedule to the equivalent form values. Intended for use client-side only.
export function scheduleTypeToForm(schedule?: ScheduleType): ScheduleCreateForm | undefined {
  if (!schedule) {
    return undefined;
  }

  // Parse rrule
  const rule = rrulestr(schedule.recurrence);
  const recurrenceSection = rruleToRecurrenceSection(rule);
  const duration = schedule.taskPayload.dueDate?.duration;
  const perUser = schedule.taskPayload.formPerUser;
  const startDate = dateOrTimestampAsDate(schedule.startDate);

  const timeOfDateString = dayjs(startDate)
    .tz("utc")
    .set("h", rule.options.byhour[0])
    .set("m", rule.options.byminute[0]);

  return {
    ...schedule,
    startDate: startDate!.toISOString(),
    frequencySimplified: schedule.frequencySimplified,
    _assignType: schedule.taskPayload.assignedTo?.length
      ? perUser
        ? "user-each"
        : "user-shared"
      : schedule.taskPayload.assignedToRole
        ? perUser
          ? "role-each"
          : "role-shared"
        : "user-shared",
    _dueDate: duration ? "overdue-after" : "none",
    _dueDateDuration: duration ? durationToQuantity(duration) : undefined,
    _dueDateUnit: duration ? durationToUnit(duration) : undefined,
    _recurrence: recurrenceSection,
    _timeOfDay: timeOfDateString.toISOString(),
  };
}

export const _forTestOnly = {
  rruleToRecurrenceSection,
};
